/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { Button, Input, Tooltip, Modal, Spin, message } from 'antd';
import Checkbox from 'components/Checkbox';
import Dropdown from 'components/CheckboxDropdown';
import ScheduleModal from 'components/ScheduleModal';
import dayjs, { Dayjs } from 'dayjs';
import { FileUploadIcon, ExclamationIcon, IconPicture } from 'components/Elements/CustomSVGIcon';
import { CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { fetchCareHomes } from 'core/assessment/assessmentApi';
import { createNews, getAllNews } from 'core/news/newsApi';
import { clearNewsState } from 'core/news/newsSlice';
import { CombinedStates, AppDispatch } from 'core/types';
import { useDispatch, useSelector } from 'react-redux';
import { CreateNewsPayload, AUDIENCE_CARE_HOME_USERS, AUDIENCE_SELECTED_CARE_HOMES } from 'core/news/types';
import { CareHomeOption } from 'components/SelectedCareHomes/types';
import Main from '../../components/layout/Main';
import PhonePreview from '../../components/PhonePreview';
import NewsUpdatesTable from '../../components/NewsUpdatesTable';
import SelectedCareHomes from '../../components/SelectedCareHomes';
import './newsBuilder.scss';

const { TextArea } = Input;

const NewsBuilder: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { careHomes } = useSelector((state: CombinedStates) => state.assessment);
    const { success, error, isCreating, publishedArticles, scheduledArticles } = useSelector((state: CombinedStates) => state.news);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [fileInfo, setFileInfo] = useState<{ name: string; size: string } | null>(null);
    const [title, setTitle] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [link, setLink] = useState<string>('');
    const [linkLabel, setLinkLabel] = useState<string>('');
    const [sendToAll, setSendToAll] = useState<boolean>(false);
    const [pushNotif, setPushNotif] = useState<boolean>(false);
    const [selectedCareHomes, setSelectedCareHomes] = useState<Array<{ value: string; label: string }>>([]);
    const [pushNotifTitle, setPushNotifTitle] = useState<string>('');
    const [pushNotifDescription, setPushNotifDescription] = useState<string>('');
    const [validationModalVisible, setValidationModalVisible] = useState<boolean>(false);
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
    const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
    const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(null);
    const [hasScheduleSelected, setHasScheduleSelected] = useState(false);

    useEffect(() => {
        dispatch(fetchCareHomes());
        dispatch(getAllNews());
    }, [dispatch]);

    const resetForm = () => {
        setImageUrl('');
        setFileInfo(null);
        setTitle('');
        setContent('');
        setLink('');
        setLinkLabel('');
        setSendToAll(false);
        setPushNotif(false);
        setSelectedCareHomes([]);
        setPushNotifTitle('');
        setPushNotifDescription('');
        setScheduleDate(null);
        setScheduleTime(null);
        setHasScheduleSelected(false);
    };

    useEffect(() => {
        if (success) {
            resetForm();
            dispatch(clearNewsState());
        }
    }, [success, dispatch]);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const onImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                message.error('Please upload an image file');
                return;
            }

            // Store file information
            const extension = file.name.split('.').pop()?.toLowerCase() || '';
            setFileInfo({
                name: `image.${extension}`,
                size: formatFileSize(file.size),
            });

            const reader = new FileReader();
            reader.onload = () => setImageUrl(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImageUrl('');
        setFileInfo(null);
    };

    const handleCareHomeSelect = (homes: Array<{ value: string; label: string }>) => {
        setSelectedCareHomes(homes);
    };

    const validateFields = () => {
        const missing: string[] = [];

        if (!title.trim()) {
            missing.push('Title');
        }

        if (!content.trim()) {
            missing.push('Content');
        }

        if (!sendToAll && selectedCareHomes.length === 0) {
            missing.push('Care Home selection');
        }

        if (pushNotif) {
            if (!pushNotifTitle.trim()) {
                missing.push('Push notification title');
            }

            if (!pushNotifDescription.trim()) {
                missing.push('Push notification description');
            }
        }

        setMissingFields(missing);
        return missing;
    };

    const handlePublish = () => {
        const missing = validateFields();

        if (missing.length > 0) {
            setValidationModalVisible(true);
            return;
        }

        const careHomeIds = selectedCareHomes.map(home => parseInt(home.value, 10));
        const publishAt = dayjs().add(5, 'seconds').format('YYYY-MM-DDTHH:mm:ss');
        const payload: CreateNewsPayload = {
            title,
            text: content,
            audience: sendToAll ? AUDIENCE_CARE_HOME_USERS : AUDIENCE_SELECTED_CARE_HOMES,
            push_notification: pushNotif,
            send_to_all_care_homes: sendToAll,
            publish_at: publishAt,
            status: 'active',
        };

        if (imageUrl) payload.media_base64 = imageUrl;
        if (link) payload.link = link;
        if (linkLabel) payload.link_label = linkLabel;
        if (pushNotif && pushNotifTitle) payload.notification_title = pushNotifTitle;
        if (pushNotif && pushNotifDescription) payload.notification_description = pushNotifDescription;
        if (!sendToAll && careHomeIds.length > 0) payload.care_home_ids = careHomeIds;
        dispatch(createNews(payload));
    };

    const handleSchedule = () => {
        const missing = validateFields();
        if (missing.length > 0) {
            setValidationModalVisible(true);
            return;
        }
        setScheduleModalVisible(true);
    };

    const handleScheduleSave = () => {
        if (!scheduleDate || !scheduleTime) {
            return;
        }

        setScheduleModalVisible(false);
        setHasScheduleSelected(true);
    };

    const handleCreateScheduled = () => {
        if (!scheduleDate || !scheduleTime) {
            return;
        }

        const dateTimeString = `${scheduleDate.format('YYYY-MM-DD')}T${scheduleTime.format('HH:mm:ss')}`;

        const careHomeIds = selectedCareHomes.map(home => parseInt(home.value, 10));

        const payload: CreateNewsPayload = {
            title,
            text: content,
            audience: sendToAll ? AUDIENCE_CARE_HOME_USERS : AUDIENCE_SELECTED_CARE_HOMES,
            push_notification: pushNotif,
            send_to_all_care_homes: sendToAll,
            publish_at: dateTimeString,
            status: 'inactive',
        };

        if (imageUrl) payload.media_base64 = imageUrl;
        if (link) payload.link = link;
        if (linkLabel) payload.link_label = linkLabel;
        if (pushNotif && pushNotifTitle) payload.notification_title = pushNotifTitle;
        if (pushNotif && pushNotifDescription) payload.notification_description = pushNotifDescription;
        if (!sendToAll && careHomeIds.length > 0) payload.care_home_ids = careHomeIds;
        dispatch(createNews(payload));
        setHasScheduleSelected(false);
    };

    const handleScheduleCancel = () => {
        setScheduleModalVisible(false);
    };

    const closeModal = () => {
        setValidationModalVisible(false);
    };

    const isFieldMissing = (fieldName: string) => missingFields.includes(fieldName);

    const careHomeOptions: CareHomeOption[] = (careHomes ?? []).map(careHome => ({
        label: careHome.title,
        value: careHome.id.toString(),
    }));

    return (
        <Main
            className="news-builder-page"
            title={<div>News Builder</div>}
            headerTitle="News Builder"
            floatingHeader={false}
            footerContent={(
                <div style={{ lineHeight: '30px', fontSize: '16px', float: 'right' }}>
                    &copy; copyright 2024
                </div>
            )}
            backLink={<Button type="link">Back</Button>}
            menuPlacement="left"
            menuWidth={0}>
            <h3 className="news-builder-title">News Builder</h3>
            <Modal
                title="Missed Field"
                open={validationModalVisible}
                onCancel={closeModal}
                footer={[
                    <Button
                        key="ok"
                        type="primary"
                        onClick={closeModal}
                        style={{
                            width: '100%',
                            border: '1px solid #0079C1',
                            background: 'transparent',
                            color: '#0079C1',
                            fontSize: '16px',
                            fontWeight: 500,
                            height: '40px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            marginTop: '10px',
                        }}>
                        OK
                    </Button>,
                ]}
                closable
                maskClosable
                centered
                width={450}
                className="validation-modal">
                <p style={{ fontSize: '15px', fontFamily: 'Roboto', margin: 0 }}>You have missed a mandatory field on the News Builder. You cannot publish or schedule your news update until all necessary fields are complete.</p>
            </Modal>
            <ScheduleModal
                visible={scheduleModalVisible}
                date={scheduleDate}
                time={scheduleTime}
                onDateChange={setScheduleDate}
                onTimeChange={setScheduleTime}
                onSave={handleScheduleSave}
                onCancel={handleScheduleCancel} />
            <div className={`news-builder ${scheduleModalVisible || validationModalVisible ? 'modal-open' : ''} ${isCreating ? 'loading' : ''}`}>
                {isCreating && (
                    <div className="loading-overlay">
                        <Spin size="large" />
                    </div>
                )}
                <div className="builder-form">
                    <div>
                        <label>Image (Optional)</label>
                        <div className="image-upload">
                            <input type="file" accept="image/*" onChange={onImageUpload} />
                            {!imageUrl && (
                                <div className="upload-icon">
                                    <FileUploadIcon />
                                    <div className="upload-text">Click here to upload an image</div>
                                </div>
                            )}
                            {imageUrl && fileInfo && (
                                <div className="image-preview-container">
                                    <div className="file-info">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <IconPicture />
                                            <div className="file-name">{fileInfo.name}</div>
                                        </div>
                                        <div className="file-details">
                                            <div className="file-size">{fileInfo.size}</div>
                                        </div>
                                    </div>
                                    <div className="remove-image-button" onClick={removeImage}>
                                        <CloseOutlined />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label>Title</label>
                        <Input
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            status={isFieldMissing('Title') ? 'error' : ''}
                            style={isFieldMissing('Title')
                                ? { border: '1px solid #ff4d4f' }
                                : {}} />
                    </div>

                    <div>
                        <label>Content</label>
                        <TextArea
                            rows={4}
                            value={content}
                            onChange={e => setContent(e.target.value)}
                            status={isFieldMissing('Content') ? 'error' : ''}
                            style={isFieldMissing('Content')
                                ? { border: '1px solid #ff4d4f' }
                                : {}} />
                    </div>

                    <div>
                        <label>Link (Optional)</label>
                        <Input value={link} onChange={e => setLink(e.target.value)} />
                    </div>

                    <div>
                        <label>Link Label (Optional)</label>
                        <Input value={linkLabel} onChange={e => setLinkLabel(e.target.value)} />
                    </div>
                    <div className="checkbox-group">
                        <div className="checkbox-item">
                            <Checkbox checked={sendToAll} onChange={() => setSendToAll(!sendToAll)} />
                            <span>Send to all care homes</span>
                        </div>
                        {!sendToAll && (
                            <div className="care-home-dropdown">
                                <Dropdown
                                    options={careHomeOptions}
                                    onSelect={handleCareHomeSelect}
                                    placeholder="Select Care Home(s) to Receive Update"
                                    styles={{
                                        border: isFieldMissing('Care Home selection') ? '1px solid #ff4d4f' : '1px solid #83ACCF',
                                        height: '50px',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                    }}
                                    values={selectedCareHomes} />
                                <SelectedCareHomes
                                    selectedCareHomes={selectedCareHomes}
                                    onRemove={(careHome) => {
                                        setSelectedCareHomes(
                                            selectedCareHomes.filter(home => home.value !== careHome.value),
                                        );
                                    }}
                                    label="Care Homes Selected:"
                                    containerStyle={{ marginTop: '12px', flexDirection: 'column', alignItems: 'flex-start' }} />
                            </div>
                        )}
                        <div className="checkbox-item">
                            <Checkbox checked={pushNotif} onChange={() => setPushNotif(!pushNotif)} />
                            <span>Send push notifications when published</span>
                        </div>
                    </div>
                    {pushNotif && (
                        <>
                            <div>
                                <div className="label-with-icon">
                                    <label>Title</label>
                                    <Tooltip
                                        title="Enter a title for the push notification which will be sent out to users devices."
                                        overlayInnerStyle={{ backgroundColor: '#00AEEF', color: 'white', width: '300px' }}
                                        mouseEnterDelay={0.1}
                                        mouseLeaveDelay={0.1}
                                        trigger="hover">
                                        <span className="info-icon">
                                            <ExclamationIcon w={25} h={25} style={{ verticalAlign: 'middle', position: 'relative', top: '-1px' }} />
                                        </span>
                                    </Tooltip>
                                </div>
                                <Input
                                    value={pushNotifTitle}
                                    onChange={e => setPushNotifTitle(e.target.value)}
                                    status={isFieldMissing('Push notification title') ? 'error' : ''} />
                            </div>
                            <div>
                                <div className="label-with-icon">
                                    <label>Push notification description</label>
                                    <Tooltip
                                        title="Enter a description of the push notification which will be sent out to users devices."
                                        overlayInnerStyle={{ backgroundColor: '#00AEEF', color: 'white', width: '300px' }}
                                        mouseEnterDelay={0.1}
                                        mouseLeaveDelay={0.1}
                                        trigger="hover">
                                        <span className="info-icon">
                                            <ExclamationIcon w={25} h={25} style={{ verticalAlign: 'middle', position: 'relative', top: '-1px' }} />
                                        </span>
                                    </Tooltip>
                                </div>
                                <TextArea
                                    rows={3}
                                    value={pushNotifDescription}
                                    onChange={e => setPushNotifDescription(e.target.value)}
                                    status={isFieldMissing('Push notification description') ? 'error' : ''} />
                            </div>
                        </>
                    )}
                    {hasScheduleSelected && (
                        <div className="scheduled-info">
                            <ClockCircleOutlined style={{ color: '#0079C1', marginRight: 8, fontSize: '20px' }} />
                            <span style={{ fontSize: '16px', fontFamily: 'Roboto', fontWeight: 600 }}>Post Scheduled:</span> {scheduleDate?.format('DD/MM/YYYY')} | {scheduleTime?.format('HH:mm A')}
                        </div>
                    )}
                    <div className="button-group">
                        {!hasScheduleSelected && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handlePublish}
                                    loading={isCreating}
                                    disabled={isCreating}
                                    block>
                                    Publish Now
                                </Button>
                                <Button
                                    onClick={handleSchedule}
                                    disabled={isCreating}
                                    block>
                                    Schedule for Later
                                </Button>
                            </>
                        )}
                        {hasScheduleSelected && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handleCreateScheduled}
                                    loading={isCreating}
                                    disabled={isCreating}
                                    block>
                                    Schedule
                                </Button>
                                <Button
                                    onClick={handleSchedule}
                                    disabled={isCreating}
                                    block>
                                    Update Schedule
                                </Button>
                            </>
                        )}
                    </div>
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}
                </div>
                <div className="builder-preview">
                    <h3>Mobile Preview</h3>
                    <PhonePreview
                        imageUrl={imageUrl}
                        title={title}
                        content={content}
                        link={link}
                        linkLabel={linkLabel} />
                </div>
            </div>
            <div className="news-updates-tables">
                <NewsUpdatesTable mode="scheduled" articles={scheduledArticles} careHomes={careHomes} />
                <NewsUpdatesTable mode="published" articles={publishedArticles} careHomes={careHomes} />
            </div>
        </Main>
    );
};

export default NewsBuilder;
